const getDb = function (dbName) {
    return new Promise((resolve, reject) => {
        if (window.__dexieInstances && window.__dexieInstances[dbName]) {
            resolve(window.__dexieInstances[dbName]);
        } else {
            console.error("Blazor.IndexedDB.Framework - Database doesn't exist:", dbName);
            reject("No database");
        }
    });
};

const getTable = function (dbName, storeName) {
    return new Promise((resolve, reject) => {
        getDb(dbName).then(db => {
            var table = db.table(storeName);
            resolve(table);
        }).catch(reject);
    });
};

const createFilterObject = function (filters) {
    const jsonFilter = {};
    for (const filter in filters) {
        if (filters.hasOwnProperty(filter))
            jsonFilter[filters[filter].indexName] = filters[filter].filterValue;
    }
    return jsonFilter;
};

export const createDb = function (dotnetReference, transaction, dbStore) {
    if (!window.__dexieInstances) window.__dexieInstances = {};

    if (window.__dexieInstances[dbStore.name])
        console.warn("Blazor.IndexedDB.Framework - Database already exists");

    var db = new Dexie(dbStore.name);

    var stores = {};
    for (var i = 0; i < dbStore.storeSchemas.length; i++) {
        var schema = dbStore.storeSchemas[i];
        var def = "";
        if (schema.primaryKeyAuto)
            def = def + "++";
        if (schema.primaryKey !== null && schema.primaryKey !== "")
            def = def + schema.primaryKey;
        if (schema.uniqueIndexes !== undefined) {
            for (var j = 0; j < schema.uniqueIndexes.length; j++) {
                def = def + ",";
                var u = "&" + schema.uniqueIndexes[j];
                def = def + u;
            }
        }
        if (schema.indexes !== undefined) {
            for (var j = 0; j < schema.indexes.length; j++) {
                def = def + ",";
                var u = schema.indexes[j];
                def = def + u;
            }
        }

        stores[schema.name] = def;
    }
    db.version(dbStore.version).stores(stores);

    // Register instance synchronously BEFORE db.open() so that other modules
    // (e.g. AssetAssemblerInterop) can find it immediately. Dexie queues
    // operations internally until open() completes, so this is safe.
    window.__dexieInstances[dbStore.name] = db;

    return db.open().then(_ => {
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Database opened');
    }).catch(e => {
        console.error(e);
        delete window.__dexieInstances[dbStore.name];
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Database could not be opened');
    });
};

export const deleteDb = function (dotnetReference, transaction, dbName) {
    getDb(dbName).then(db => {
        db.delete().then(_ => {
            if (window.__dexieInstances) delete window.__dexieInstances[dbName];
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Database deleted');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Database could not be deleted');
        });
    });
};

export const addItem = function (dotnetReference, transaction, item) {
    getTable(item.dbName, item.storeName).then(table => {
        table.add(item.record).then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Item added');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be added');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be added');
    });
};

export const bulkAddItem = function (dotnetReference, transaction, dbName, storeName, items) {
    getTable(dbName, storeName).then(table => {
        table.bulkAdd(items).then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Item(s) bulk added');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item(s) could not be bulk added');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item(s) could not be bulk added');
    });
};

export const putItem = function (dotnetReference, transaction, item) {
    getTable(item.dbName, item.storeName).then(table => {
        table.put(item.record).then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Item put successful');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item put failed');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item put failed');
    });
};

export const updateItem = function (dotnetReference, transaction, item) {
    getTable(item.dbName, item.storeName).then(table => {
        table.update(item.key, item.record).then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Item updated');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be updated');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be updated');
    });
};

export const deleteItem = function (dotnetReference, transaction, item) {
    getTable(item.dbName, item.storeName).then(table => {
        table.delete(item.key).then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Item deleted');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be deleted');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Item could not be deleted');
    });
};

export const clear = function (dotnetReference, transaction, dbName, storeName) {
    getTable(dbName, storeName).then(table => {
        table.clear().then(_ => {
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Table cleared');
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Table could not be cleared');
        });
    }).catch(e => {
        console.error(e);
        dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Table could not be cleared');
    });
};

export const findItem = function (dotnetReference, transaction, item) {
    var promise = new Promise((resolve, reject) => {
        getTable(item.dbName, item.storeName).then(table => {
            table.get(item.key).then(i => {
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'Found item');
                resolve(i);
            }).catch(e => {
                console.error(e);
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Could not find item');
                reject(e);
            });
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'Could not find item');
            reject(e);
        });
    });
    return promise;
};

export const toArray = function (dotnetReference, transaction, dbName, storeName) {
    return new Promise((resolve, reject) => {
        getTable(dbName, storeName).then(table => {
            table.toArray(items => {
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'toArray succeeded');
                resolve(items);
            }).catch(e => {
                console.error(e);
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'toArray failed');
                reject(e);
            });
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'toArray failed');
            reject(e);
        });
    });
};

export const where = function (dotnetReference, transaction, dbName, storeName, filters) {
    const filterObject = createFilterObject(filters);
    return new Promise((resolve, reject) => {
        getTable(dbName, storeName).then(table => {
            table.where(filterObject).toArray(items => {
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, false, 'where succeeded');
                resolve(items);
            }).catch(e => {
                console.error(e);
                dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'where failed');
                reject(e);
            });
        }).catch(e => {
            console.error(e);
            dotnetReference.invokeMethodAsync('BlazorDBCallback', transaction, true, 'where failed');
            reject(e);
        });
    });
};
